import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { apiClient } from "../../../../api/httpClient";
import { toast } from "react-toastify";
import { extractErrorMessage } from "../../../../globals/utils/error-handler";
import { createSupportRequest } from "../../../../services/supportRequestService";
import { validateFieldLengths } from "../../../../globals/utils/bondFieldLimits";
import {
  formatRequiredFieldLabel,
  navigateToRoleHome,
} from "./historicBondSharedHelpers";

const INITIAL_GLOBAL_STATE = {
  basicInformation: {},
  generalInformation: {},
  guardianDetails: { guardians: [] },
  attorneyInformation: {},
  obligeeInformation: {},
  requestorInformation: {},
  documentUpload: {},
  billingInformation: {},
  otherDetails: {},
};

const RESET_GLOBAL_STATE = {
  basicInformation: {},
  generalInformation: {},
  guardianDetails: { guardians: [] },
  attorneyInformation: {},
  requestorInformation: {},
  documentUpload: {},
  billingInformation: {},
  otherDetails: {},
};

function useHistoricBondCreateBase(config) {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const navigate = useNavigate();
  const [globalState, setGlobalState] = useState(INITIAL_GLOBAL_STATE);

  const userdetails = useSelector((state) => state.auth.user);
  const lawfirmdetails = useSelector((state) => state.lawfirm);
  const { role } = useSelector((state) => state.auth);

  const requiredFieldsPerStep = config.getRequiredFieldsPerStep(globalState);

  const validateStepData = (data, section) => {
    let requiredFields = [...(requiredFieldsPerStep[section] || [])];

    if (section === "guardianDetails") {
      const count =
        globalState.basicInformation?.[config.guardianCountField] || 1;
      for (let i = 0; i < count; i++) {
        config.guardianFieldNames.forEach((field) => {
          requiredFields.push(`guardians.${i}.${field}`);
        });
      }
    }

    if (section === "billingInformation" && data?.billing_entity === "Other") {
      requiredFields.push(
        "billing_other_name",
        "billing_other_address",
        "billing_other_city",
        "billing_other_state",
        "billing_other_zip"
      );
    }

    const sectionErrors = {};
    requiredFields.forEach((path) => {
      const parts = path.split(".");
      let cur = data;
      for (const part of parts) {
        if (cur?.[part] === undefined || cur?.[part] === "") {
          sectionErrors[path] = "This field is required";
          break;
        }
        cur = cur[part];
      }
    });

    const lengthErrors = validateFieldLengths(
      data,
      config.sectionLimitsMap[section]
    );
    Object.assign(sectionErrors, lengthErrors);

    return sectionErrors;
  };

  const handleFinish = async () => {
    setIsLoading(true);
    let payload = config.buildPayload(globalState, {
      userdetails,
      lawfirmdetails,
    });

    if (config.preparePayload) {
      payload = config.preparePayload(payload, globalState);
    }

    try {
      await apiClient(config.apiEndpoint, "POST", payload);
      setIsLoading(false);
      toast.success("Bond created successfully!");
      setGlobalState(RESET_GLOBAL_STATE);
      setErrors({});
      navigateToRoleHome(navigate, role);
    } catch (error) {
      setIsLoading(false);
      const message = extractErrorMessage(error);
      console.error("Error submitting or approving the bond:", error);
      setSubmissionError(message);
      setSupportModalOpen(true);
    }
  };

  const handleSupportRequestSubmit = async (comment) => {
    try {
      const hydrationPayload = { state: globalState };
      await createSupportRequest({
        request_body: JSON.stringify(hydrationPayload),
        bond_type: config.supportBondType,
        name: config.getSupportName(globalState),
        user_comment: comment,
      });
      toast.success("Support request created successfully.");
      setSupportModalOpen(false);
    } catch (err) {
      console.error("Failed to create support request:", err);
      toast.error("Failed to create support request.");
    }
  };

  const updateGlobalState = (stepKey, stepData) => {
    setGlobalState((prevState) => ({
      ...prevState,
      [stepKey]:
        stepKey === "guardianDetails"
          ? {
              ...stepData,
              guardians: Array.isArray(stepData.guardians)
                ? stepData.guardians
                : [],
            }
          : stepData,
    }));
    Object.keys(stepData).forEach((fieldName) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: "",
      }));
    });
  };

  const handleSubmit = async () => {
    const allErrors = {};
    Object.keys(globalState).forEach((section) => {
      const sectionErrors = validateStepData(globalState[section], section);
      Object.assign(allErrors, sectionErrors);
    });

    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);

      const lengthErrors = Object.entries(allErrors)
        .filter(([, msg]) => msg.includes("characters or less"))
        .map(([, msg]) => msg);
      const requiredErrors = Object.entries(allErrors).filter(
        ([, msg]) => !msg.includes("characters or less")
      );

      if (lengthErrors.length > 0) {
        lengthErrors.forEach((msg) => toast.warning(msg));
      }

      if (requiredErrors.length > 0) {
        const errorFields = requiredErrors
          .map(([field]) =>
            formatRequiredFieldLabel(field, config.guardianErrorFieldLabels)
          )
          .join(", ");
        toast.error(`Please complete the following fields: ${errorFields}`);
      }
      return;
    }

    await handleFinish();
  };

  return {
    isLoading,
    errors,
    setErrors,
    supportModalOpen,
    setSupportModalOpen,
    submissionError,
    globalState,
    updateGlobalState,
    handleSubmit,
    handleSupportRequestSubmit,
  };
}

export default useHistoricBondCreateBase;
export { ROLE_PATH_MAP } from "./historicBondSharedHelpers";
